import { getPageSuggestions } from 'components/Chat/instructor-hugo/pageConfig';
import { useAuth } from 'contexts/auth';
import { useWebsocket } from 'contexts/websocket';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildSessionContext } from 'utils/chat/instructorHugoContext';
import {
    createLocalChatShell,
    getChatTitle,
    getFirstName,
    getRouteChatKey,
    shouldShowPageSuggestions,
} from './useInstructorHugoChatHelpers';
import useInstructorHugoChatContext from './useInstructorHugoContext';
import useInstructorHugoMessageLifecycle from './useInstructorHugoMessageLifecycle';
import useInstructorHugoSync from './useInstructorHugoSync';
import useInstructorHugoThreads from './useInstructorHugoThreads';

const useInstructorHugoChat = ({ isOpen, pathname }) => {
    const { auth, currentUser } = useAuth();
    const { t } = useTranslation();
    const { isConnected, onMessage, setPingPayload } = useWebsocket();

    const [sessionContext, setSessionContext] = useState(() => buildSessionContext({ auth, pathname }));
    const { currentRouteChatKey, derivedSessionContext, outgoingContext, outgoingPageContext, scope } =
        useInstructorHugoChatContext({
            auth,
            pathname,
            sessionContext,
        });
    const [activeRouteChatKey, setActiveRouteChatKey] = useState(() =>
        getRouteChatKey({ auth, sessionContext: buildSessionContext({ auth, pathname }) })
    );
    const [chat, setChat] = useState(() => createLocalChatShell({ auth, scope: sessionContext.scope }));
    const isChatRouteSynced = activeRouteChatKey === currentRouteChatKey;
    const chatRef = useRef(chat);

    const threadControls = useInstructorHugoThreads({
        auth,
        chat,
        chatRef,
        scope,
        setChat,
        t,
    });
    const { bindLocalChatToPersistedChat, loadThreadSummaries, onDeleteThread, threadListAdapter, threadSwitcher } =
        threadControls;

    const { reconcilePersistedAssistantMessage, refreshActiveThread, setRuntimeBlocksReconciliation } =
        useInstructorHugoSync({
            chat,
            chatRef,
            isConnected,
            isOpen,
            loadThreadSummaries,
            onMessage,
            setChat,
            setPingPayload,
        });

    const { buildOutgoingRequest, onChatRequestStateChange } = useInstructorHugoMessageLifecycle({
        bindLocalChatToPersistedChat,
        chatRef,
        loadThreadSummaries,
        outgoingContext,
        outgoingPageContext,
        reconcilePersistedAssistantMessage,
        refreshActiveThread,
        scope,
        setChat,
        setRuntimeBlocksReconciliation,
    });

    useEffect(() => {
        if (!isOpen) return;

        loadThreadSummaries();

        if (isChatRouteSynced) return;

        setActiveRouteChatKey(currentRouteChatKey);
        setSessionContext(derivedSessionContext);
        setChat(createLocalChatShell({ auth, scope: derivedSessionContext.scope }));
    }, [auth, currentRouteChatKey, derivedSessionContext, isChatRouteSynced, isOpen, loadThreadSummaries]);

    const pageSuggestions = useMemo(
        () => getPageSuggestions(outgoingPageContext.page_type, t),
        [outgoingPageContext.page_type, t]
    );
    const showPageSuggestions = shouldShowPageSuggestions(chat);

    return {
        buildOutgoingRequest,
        chat,
        isChatRouteSynced,
        isBootstrapping: false,
        onChatRequestStateChange,
        onDeleteThread,
        onRefreshThreads: loadThreadSummaries,
        showSuggestions: showPageSuggestions,
        suggestions: pageSuggestions,
        suggestionsTitle: t('chat.hugo.quick-ideas-title', 'Quick ideas to get you started'),
        threadListAdapter,
        threadSwitcher,
        viewModel: {
            composer: {
                placeholder: t('chat.hugo.placeholder-label', 'Help me with this page'),
            },
            emptyState: {
                body: t('chat.hugo.greeting-assignment-message', 'How can I help with your assignment?'),
                firstName: getFirstName({ auth, currentUser }),
                isVisible: showPageSuggestions,
            },
            header: {
                title: getChatTitle(chat) || t('chat.hugo.title', 'Hugo'),
            },
        },
        assistantIdentity: {
            icon: 'hugo',
            label: t('chat.hugo.title', 'Hugo'),
        },
    };
};

export default useInstructorHugoChat;
